"use client";

import {
  EDURATION_TYPE,
  EPackageStatus,
  ICreatePackageDTO,
  IPackage,
} from "@/utilities/types/Package";
import {
  ActionIcon,
  Button,
  Input,
  Select,
  SelectOption,
  Switch,
  Textarea,
  Title,
} from "rizzui";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { PiXBold } from "react-icons/pi";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";

export interface PackageFormProps {
  initialData?: IPackage | null;
  isLoading: boolean;
  onSubmit: (data: ICreatePackageDTO) => Promise<void>;
  isUpdate?: boolean;
  onClose?: () => void;
  isModal?: boolean;
}

export default function PackageForm({
  initialData,
  isLoading,
  onSubmit,
  isUpdate = false,
  onClose,
  isModal = false,
}: PackageFormProps) {
  const navigate = useNavigate();
  const title = isUpdate ? "Edit Package" : "Create Package";

  const form = useForm<ICreatePackageDTO>({
    defaultValues: {
      name: "",
      description: "",
      code: "",
      price: 0,
      numberOfCredit: 0,
      duration: 0,
      discount: 0,
      status: EPackageStatus.ACTIVE,
      isPopular: false,
      isHidden: false,
      durationType: EDURATION_TYPE.DAY,
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name || "",
        description: initialData.description || "",
        price: initialData.price || 0,
        code: initialData.code,
        numberOfCredit: initialData.numberOfCredit || 0,
        duration: initialData.duration || 0,
        discount: initialData.discount || 0,
        status: initialData.status || EPackageStatus.ACTIVE,
        isPopular: !!initialData?.isPopular,
        isHidden: initialData?.isHidden,
        durationType: initialData?.durationType || EDURATION_TYPE.DAY,
      });
    }
  }, [initialData, reset]);

  const handleFormSubmit = async (data: ICreatePackageDTO) => {
    try {
      await onSubmit(data);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Something went wrong";
      toast.error(errorMessage);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="grid grid-cols-1 gap-6 p-6 @container md:grid-cols-2 [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
      noValidate
    >
      <div className="col-span-full flex items-center justify-between">
        <Title as="h5" className="@xl:basis-auto text-xl md:text-2xl">
          {title}
        </Title>
        {isModal && onClose && (
          <ActionIcon size="sm" variant="text" onClick={onClose}>
            <PiXBold className="h-auto w-5" />
          </ActionIcon>
        )}
      </div>

      <div className="col-span-2 grid gap-4 md:grid-cols-3">
        <Controller
          name="name"
          control={control}
          rules={{ required: "Name is required" }}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Name"
              size="lg"
              placeholder="Enter package name"
              disabled={isLoading}
              {...field}
              className="[&>label>span]:font-medium "
              error={error?.message}
            />
          )}
        />

        <Controller
          name="code"
          control={control}
          rules={{
            required: "Code is required",
            min: { value: 0, message: "Code must be greater than 0" },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Code"
              size="lg"
              type="text"
              placeholder="Enter code"
              disabled={!!initialData}
              {...field}
              onChange={(e) => {
                const sanitizedValue = e.target.value.replace(/\s+/g, "");
                field.onChange(sanitizedValue);
              }}
              className="[&>label>span]:font-medium"
              error={error?.message}
            />
          )}
        />

        <Controller
          name="price"
          control={control}
          rules={{
            required: "Price is required",
            min: { value: 0, message: "Price must be greater than 0" },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Price"
              size="lg"
              type="number"
              placeholder="Enter price"
              suffix="$"
              disabled={isLoading}
              {...field}
              onChange={(e) => field.onChange(Number(e.target.value))}
              className="[&>label>span]:font-medium "
              error={error?.message}
            />
          )}
        />
      </div>

      <Controller
        name="description"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Textarea
            label="Description"
            placeholder="Enter package description"
            disabled={isLoading}
            {...field}
            className="[&>label>span]:font-medium col-span-full"
            error={error?.message}
          />
        )}
      />

      <div className="col-span-2 grid gap-4 md:grid-cols-3">
        <Controller
          name="duration"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Duration (days/months)"
              size="lg"
              placeholder="Enter duration in days"
              type="number"
              disabled={isLoading}
              suffix={
                <Controller
                  name="durationType"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <Select
                        size="sm"
                        variant="text"
                        options={[
                          { value: EDURATION_TYPE.DAY, label: "day" },
                          { value: EDURATION_TYPE.MONTH, label: "month" },
                        ]}
                        value={field.value}
                        displayValue={(value) =>
                          Number(watch("duration")) > 1 ? (
                            <>{`${value}s`.toLowerCase()}</>
                          ) : (
                            <>{`${value}`.toLowerCase()}</>
                          )
                        }
                        onChange={(v: {
                          value: EDURATION_TYPE;
                          label: string;
                        }) => {
                          field.onChange(v.value);
                        }}
                        className="w-[100px]"
                      />
                    );
                  }}
                />
              }
              {...field}
              onChange={(e) => field.onChange(Number(e.target.value))}
              className="[&>label>span]:font-medium"
              error={error?.message}
            />
          )}
        />

        <Controller
          name="discount"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Discount (%)"
              size="lg"
              placeholder="Enter discount percentage"
              type="number"
              disabled={isLoading}
              {...field}
              onChange={(e) => field.onChange(Number(e.target.value))}
              className="[&>label>span]:font-medium"
              error={error?.message}
            />
          )}
        />
        <Controller
          name="status"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Select
              label="Status"
              options={[
                { label: "Active", value: EPackageStatus.ACTIVE },
                { label: "Inactive", value: EPackageStatus.INACTIVE },
              ]}
              placeholder="Select status"
              value={field.value}
              onChange={(option: SelectOption) => {
                field.onChange(option.value);
              }}
              disabled={isLoading}
              displayValue={(value) =>
                value === EPackageStatus.ACTIVE ? "Active" : "Inactive"
              }
              className="[&>label>span]:font-medium col-span-full md:col-span-1"
              error={error?.message}
            />
          )}
        />
        <div className="flex items-center gap-8">
          <Controller
            name="isPopular"
            control={control}
            render={({ field, fieldState: { error }, formState }) => (
              <Switch
                label="Popular"
                size="lg"
                className=" w-auto"
                disabled={isLoading}
                checked={!!field?.value}
                error={error?.message}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="isHidden"
            control={control}
            render={({ field, fieldState: { error }, formState }) => (
              <Switch
                label="Is Hidden"
                size="lg"
                className=" w-auto"
                disabled={isLoading}
                checked={!!field?.value}
                error={error?.message}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      <div className="col-span-full flex items-center justify-end gap-4">
        <Button
          variant="outline"
          onClick={
            isModal && onClose
              ? onClose
              : () => navigate(routes.package.listing)
          }
          className="md:w-auto w-1/2"
          type="button"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          className="md:w-auto w-1/2"
          disabled={isLoading || !isDirty}
        >
          {title}
        </Button>
      </div>
    </form>
  );
}
